import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';

interface NavItem {
  path: string;
  label: string;
  icon: string;
}

const roleLabels: Record<string, string> = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  moderator: 'Modérateur',
  business: 'Entreprise',
  freelancer: 'Freelance',
  instructor: 'Formateur',
  user: 'Utilisateur',
};

const commonItems: NavItem[] = [
  { path: '/dashboard', label: 'Tableau de bord', icon: 'ri-dashboard-line' },
  { path: '/messages', label: 'Messagerie', icon: 'ri-chat-3-line' },
  { path: '/dashboard/profile', label: 'Mon profil', icon: 'ri-user-3-line' },
  { path: '/dashboard/subscription', label: 'Mon abonnement', icon: 'ri-vip-crown-line' },
  { path: '/dashboard/media', label: 'Mon contenu média', icon: 'ri-gallery-line' },
  { path: '/cloud', label: 'Mon Cloud', icon: 'ri-cloud-line' },
];

const roleItems: Record<string, NavItem[]> = {
  business: [
    { path: '/dashboard/business', label: 'Mon entreprise', icon: 'ri-store-2-line' },
    { path: '/dashboard/rooms', label: 'Mes chambres', icon: 'ri-hotel-bed-line' },
    { path: '/dashboard/apartments', label: 'Mes appartements', icon: 'ri-home-4-line' },
    { path: '/dashboard/listings', label: 'Mes annonces', icon: 'ri-megaphone-line' },
    { path: '/dashboard/jobs', label: 'Mes offres d\u2019emploi', icon: 'ri-briefcase-4-line' },
  ],
  freelancer: [
    { path: '/dashboard/freelancer', label: 'Mon profil freelance', icon: 'ri-user-star-line' },
    { path: '/dashboard/gigs', label: 'Mes services', icon: 'ri-service-line' },
    { path: '/dashboard/gigs/orders', label: 'Mes commandes', icon: 'ri-shopping-bag-line' },
  ],
  instructor: [
    { path: '/dashboard/courses', label: 'Mes formations', icon: 'ri-graduation-cap-line' },
    { path: '/dashboard/courses/new', label: 'Nouvelle formation', icon: 'ri-add-line' },
  ],
  user: [
    { path: '/dashboard/listings', label: 'Mes annonces', icon: 'ri-megaphone-line' },
    { path: '/dashboard/apartments', label: 'Mes appartements', icon: 'ri-home-4-line' },
  ],
  admin: [
    { path: '/admin', label: 'Administration', icon: 'ri-shield-line' },
  ],
  super_admin: [
    { path: '/admin', label: 'Administration', icon: 'ri-shield-line' },
  ],
  moderator: [
    { path: '/admin', label: 'Modération', icon: 'ri-shield-line' },
  ],
};

const forumItems: NavItem[] = [
  { path: '/dashboard/forum', label: 'Mes discussions', icon: 'ri-discuss-line' },
];

function DashboardSidebar() {
  const location = useLocation();
  const { profile } = useAuth();

  const role = profile?.role ?? 'user';
  const roleLabel = roleLabels[role] ?? role;

  const navItems = [
    ...commonItems,
    ...(roleItems[role] ?? []),
    ...forumItems,
  ];

  return (
    <aside className="w-full lg:w-64 shrink-0">
      <div className="bg-background-100 border border-background-200/70 rounded-xl p-3 lg:sticky lg:top-24">
        <div className="flex items-center gap-3 px-3 py-3 mb-2">
          <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-500/10">
            <i className="ri-user-settings-line text-lg text-primary-400" />
          </div>
          <div>
            <span className="block text-sm font-semibold text-foreground-50">Mon espace</span>
            <span className="block text-xs text-foreground-400">{roleLabel}</span>
          </div>
        </div>

        <nav className="space-y-1">
          {navItems.map((item) => {
            const active =
              location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                  active
                    ? 'bg-primary-500/10 text-primary-400'
                    : 'text-foreground-500 hover:text-foreground-50 hover:bg-background-200/60'
                }`}
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className={`${item.icon} text-base`} />
                </span>
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="mt-4 pt-4 border-t border-background-200/70">
          <Link
            to="/"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-foreground-500 hover:text-foreground-50 hover:bg-background-200/60 transition-colors"
          >
            <span className="w-5 h-5 flex items-center justify-center">
              <i className="ri-arrow-left-line text-base" />
            </span>
            Retour au site
          </Link>
        </div>
      </div>
    </aside>
  );
}

export default DashboardSidebar;